<template lang="pug">
  base-content(ref="main" title="ライセンス減算＆キー発行申請書<span class=\"nopdf\">の作成</span>", :subtitle="subtitle", :type="titleType")

    b-message.nopdf(type="is-info" has-icon)
      .content
        p.is-size-5.has-text-weight-bold 以下の内容をご確認及びご同意の上、申請書の作成を行ってください。
        ul
          li 製品を利用するユーザー数が減少した場合に、ライセンス数の減算を申し込むことができます。
          li ライセンスの減算には「ライセンス証書変更手数料」の購入が必要となります。
          li 減算されたライセンスのライセンスキーは必ず運用を行われる製品に登録をお願いします。
          li 一度、減算されたライセンスを再度追加される場合は、別途追加ライセンスをご購入いただく必要がございます。

    form(@submit.prevent="submit", data-testid="form-app")
      app-message(v-if="appErrorMessage" type="danger") {{appErrorMessage}}

      button-bar(@downloadDataFile="downloadDataFile" @importData="importData")

      section.section

        form-header(v-if="nextTicker", v-model="postData.application", :showFields="showHeaderFields", :requiredFields="requiredHeaderFields")

        h3.title 申請内容

        section.section

          b-field(label="ライセンス数減算をご希望のご利用製品名")
          b-field.required(
            :message="getInputMessage('product')",
            :type="getInputType('product')"
          )
            b-select(
              v-if="isEdit",
              v-model="postData.product",
              name="product",
              v-validate="",
              data-vv-as="ライセンス数減算をご希望のご利用製品名",
              :required="true"
              placeholder="（選択してください）"
              data-testid="product"
            )
              option(v-for="item in options.product" :key="item.key" :value="item.value") {{item.label}}
            p(v-else-if="postData.product !== getOptionValueByKey(options.product, 'other')") {{getOptionLabelByValue(options.product, postData.product)}}

          b-field.required(
            :message="getInputMessage('product_other')",
            :type="getInputType('product_other')"
          )
            b-input(
              expanded
              v-if="isEdit"
              v-model="postData.product_other",
              name="product_other",
              :disabled="postData.product !== getOptionValueByKey(options.product, 'other')",
              :required="postData.product === getOptionValueByKey(options.product, 'other')",
              :maxlength="maxlen_sbt.product_other",
              v-validate="",
              data-vv-as="その他",
              placeholder="「その他」を選択した場合、こちらに入力してください。",
              data-testid="product_other"
            )
            p(v-else-if="postData.product === getOptionValueByKey(options.product, 'other')") {{postData.product_other}}

          b-field.required(
            label="ライセンス証書番号 (複数ライセンスをお持ちの場合、いずれか1つ)",
            :message="getInputMessage('c1_license_no')",
            :type="getInputType('c1_license_no')"
          )
            b-input(
              v-if="isEdit",
              v-model="postData.c1_license_no",
              name="c1_license_no",
              :required="true"
              :maxlength="maxlen_sbt.c1_license_no",
              v-validate="",
              data-vv-as="ライセンス証書番号"
              :placeholder="lang_field.licenseNoExample",
              data-testid="c1_license_no"
            )
            p(v-else) {{postData.c1_license_no}}

          app-message.nopdf(type='info')
            notice-license-number

          b-field.required(
            label="ユーザー数 (複数ライセンスをお持ちの場合、合計ユーザー数)",
            :message="getInputMessage('c1_user_count')",
            :type="getInputType('c1_user_count')"
          )
            b-input(
              v-if="isEdit",
              v-model="postData.c1_user_count",
              name="c1_user_count",
              type="number",
              :required="true",
              max="999999999",
              v-validate="",
              data-vv-as="ユーザー数"
              data-testid="c1_user_count"
            )
            p(v-else) {{postData.c1_user_count}}

          b-field.required(
            label="その他の証書有無",
            :message="getInputMessage('has_other_apps')",
            :type="getInputType('has_other_apps')"
          )
            .block(v-if="isEdit")
              b-radio(
                v-for="item in options.has_other_apps"
                v-model="postData.has_other_apps"
                :key="item.key"
                name="has_other_apps"
                :required="true"
                :native-value="item.value"
                :data-testid="'has_other_apps-' + item.key"
              ) {{item.label}}
            p(v-else)
              span {{getOptionLabelByValue(options.has_other_apps, postData.has_other_apps)}}

          b-field.required(
            label="減算後のユーザー数",
            :message="getInputMessage('user_count_after')",
            :type="getInputType('user_count_after')"
          )
            b-input(
              v-if="isEdit",
              v-model="postData.user_count_after",
              name="user_count_after",
              type="number",
              :required="true",
              max="999999999",
              v-validate="",
              data-vv-as="減算後のユーザー数"
              data-testid="user_count_after"
            )
            p(v-else) {{postData.user_count_after}}

        h3.title エンドユーザー様情報

        section.section
          app-message.nopdf(type="warning")
            .content
              ul
                li 減算後の新しいライセンスキーを下記にご記載頂いたメールアドレス宛にお送り致します。
                li そのためには製品の管理者画面上でご確認頂ける「お客様コード」が必ず必要です。
                li こちらがございませんと減算処理をお受け出来ません。

          .columns
            .column.is-half
              b-field.required(
                label="お客様コード",
                expanded,
                :message="getInputMessage('application.customer_code')",
                :type="getInputType('application.customer_code')"
              )
                b-input(
                  v-if="isEdit",
                  v-model="postData.application.customer_code",
                  name="application.customer_code",
                  :required="true",
                  :maxlength="maxlen_app.customer_code",
                  v-validate="",
                  data-vv-as="お客様コード"
                  :placeholder="lang_field.customerCodeExample"
                  data-testid="application.customer_code"
                )
                p(v-else) {{postData.application.customer_code || lang_field.empty}}

              app-message.nopdf(type='info')
                notice-customer-code

            .column.is-half
              b-field(
                label="ご利用中のサーバーのOS/DBの種類",
                expanded,
                :message="getInputMessage('os_db_type')",
                :type="getInputType('os_db_type')"
              )
                b-input(
                  v-if="isEdit",
                  v-model="postData.os_db_type",
                  name="os_db_type",
                  :maxlength="maxlen_sbt.os_db_type",
                  v-validate="",
                  data-vv-as="ご利用中のサーバーのOS/DBの種類"
                  data-testid="os_db_type"
                )
                p(v-else) {{postData.os_db_type || lang_field.empty}}

        form-user(v-if="nextTicker", v-model="postData.application" prefix="end_")

        template(v-if="isPartnerApp()")
          h3.title エンドユーザー様へ販売されるパートナー様情報
          form-user(v-if="nextTicker", v-model="postData.application" prefix="partner_")

      button-bar(@downloadDataFile="downloadDataFile" @importData="importData")
</template>

<script>
import BaseContent from '@/components/BaseContent'
import ButtonBar from '@/components/apps/com/ButtonBar'
import FormHeader from '@/components/apps/com/FormHeader'
import FormUser from '@/components/apps/com/FormUser'
import NoticeLicenseNumber from '@/components/apps/com/NoticeLicenseNumber'
import NoticeCustomerCode from '@/components/apps/com/NoticeCustomerCode'
import AppMessage from '@/components/AppMessage'
import MixinApplication from '@/mixins/application'
import MixinHandleErrors from '@/mixins/handleErrors'
import { Applications as maxlen_app, AppLicenseSubtractions as maxlen_sbt } from '@/config/maxlengths'
import { fields as lang_field } from '@/lang/ja_JP'
import Util from '@/utils/util'

export default {
  name: 'LicenseSubtraction',
  mixins: [ MixinApplication, MixinHandleErrors ],
  components: { BaseContent, ButtonBar, FormHeader, FormUser, NoticeLicenseNumber, NoticeCustomerCode, AppMessage },
  data () {
    return {
      maxlen_app,
      maxlen_sbt,
      lang_field,
      Util,

      showHeaderFields: [],
      requiredHeaderFields: [],

      postData: {
        application: {
          order_no: '',
          estimate_no: '',
          customer_code: ''
        },
        product: null,
        product_other: '',
        c1_license_no: '',
        c1_user_count: '',
        has_other_apps: '0',
        user_count_after: '',
        os_db_type: ''
      },

      /**
       * 選択肢の定義
       * options[ここはフィールド名]
       */
      options: {
        product: [
          {
            key: 'neo',
            label: 'desknet\'s NEO',
            value: 'p'
          },
          {
            key: 'app',
            label: 'AppSuite',
            value: 'q'
          },
          {
            key: 'web',
            label: 'ウェブ会議',
            value: 'r'
          },
          {
            key: 'chatLuck',
            label: 'ChatLuck',
            value: 'l'
          },
          {
            key: 'deskCams',
            label: 'desknet\'s CAMS',
            value: 'j'
          },
          {
            key: 'denbun',
            label: 'DenbunPOP/IMAP',
            value: 'k'
          },
          {
            key: 'other',
            label: 'その他',
            value: 'n'
          }
        ],
        has_other_apps: [
          {
            key: 'exists',
            label: '有',
            value: '1'
          },
          {
            key: 'none',
            label: '無',
            value: '0'
          }
        ]
      },

      // PDF作成処理失敗によるエラーメッセージ
      appErrorMessage: '',
      appInfoMessage: ''
    }
  },
  created () {
    this.mode = 'edit'
    this.postData.application = Object.assign(this.postData.application, this.postData__application)
    this.postData.application.app_code = 'sbt'

    if (this.isPartnerApp()) {
      // AppHeaderの部分を調整
      this.showHeaderFields = ['order_date', 'order_no', 'notes']
      this.requiredHeaderFields = ['order_date']
      delete this.postData.application.estimate_no
    } else {
      // AppHeaderの部分を調整
      this.showHeaderFields = ['order_date', 'estimate_no', 'notes']
      this.requiredHeaderFields = ['order_date']
      delete this.postData.application.order_no
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/apps/LicenseSubtraction.vue